import { AsyncPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Language } from '@lx/language';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { SchoolLocationService } from '../../services';
import { CourseState, CourseStore, SharedDataStore } from '../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { CoursePayload, GroupedSchoolLocation } from '../../types';
import { SchoolLocation } from '@lx/quote-tool/lite/shared/types';
import { AdultCoursesComponent } from '../adult-courses/adult-courses.component';
import { LocationSelectorFieldComponent } from '../location-selector-field/location-selector-field.component';
import { SingleSelectionButtonListFieldComponent } from '../single-selection-button-list-field/single-selection-button-list-field.component';
import { getCourseSelectionProviders } from '../../utils';

@Component({
  standalone: true,
  selector: 'lx-language-schools',
  templateUrl: './language-schools.component.html',
  styleUrls: ['./language-schools.component.scss'],
  imports: [SingleSelectionButtonListFieldComponent, LocationSelectorFieldComponent, AsyncPipe, FormsModule, NgIf, AdultCoursesComponent],
  providers: [...getCourseSelectionProviders()],
})
export class LanguageSchoolsComponent implements OnInit {
  @Output() coursePayload = new EventEmitter<CoursePayload>();
  @Output() validCoursePayload = new EventEmitter<boolean>();

  languages$: Observable<Language[]> | undefined;
  groupedSchoolLocations$: Observable<GroupedSchoolLocation[]> | undefined;

  automaticOpenDatePanel$: Observable<boolean> | undefined;
  courseDetails$: Observable<Partial<CourseState>> | undefined;

  private readonly selectedDuration$ = new BehaviorSubject<number | undefined>(undefined);

  private readonly courseStore = inject(CourseStore);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly schoolLocationService = inject(SchoolLocationService);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.languages$ = this.sharedDataStore.languages$;
    this.courseDetails$ = this.courseStore.courseDetails$;
  }

  ngOnInit(): void {
    this.courseStore.selectedDuration$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.selectedDuration$);

    this.automaticOpenDatePanel$ = combineLatest([this.courseStore.selectedStartDate$, this.selectedDuration$]).pipe(
      map(([selectedStartDate, selectedDuration]) => selectedStartDate === undefined && selectedDuration !== undefined),
    );

    this.courseStore.isAdultValid$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((valid) => this.validCoursePayload.emit(valid));

    this.courseDetails$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((courseDetails) => this.coursePayload.emit(courseDetails));

    this.groupedSchoolLocations$ = this.schoolLocationService.getGroupedSchoolLocations();
  }

  updateSelectedLanguage(selectedLanguage: string) {
    this.updateValueInStore(selectedLanguage, 'selectedLanguage');
  }

  updateSelectedSchoolLocation(schoolLocation: SchoolLocation) {
    this.updateValueInStore(schoolLocation, 'selectedSchoolLocation');
  }

  updateSelectedCourse(selectedCourse: CourseDetails) {
    this.updateValueInStore(selectedCourse, 'selectedCourse');
  }

  updateSelectedDuration(selectedDuration: number) {
    this.updateValueInStore(selectedDuration, 'selectedDuration');
    this.selectedDuration$.next(selectedDuration);
  }

  updateSelectedStartDate(selectedStartDate: string) {
    this.updateValueInStore(selectedStartDate, 'selectedStartDate');
  }

  private updateValueInStore(value: string | SchoolLocation | CourseDetails | number | undefined, key: keyof CoursePayload) {
    this.courseStore.updateState({
      [key]: value,
    });
  }
}
